import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { PlatformChartCycleMomentsDto } from '@data/_generated-api/platform-api/models/platform-chart-cycle-moments-dto';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HdChartDataCache, cycleMomentsKey } from '../../library/hd-chart-data-cache.service';
import { HD_CYCLE_GLYPHS, HD_CYCLE_ICONS, HD_JUST_NOW_ICONS, hdCycleLabel } from '../../library/hd-chart-cycle-metadata';
import { extractCycleSourceCore, mergeCycleConfiguration } from '../../library/hd-cycle-chart.utils';
import { HdBodygraphChartPreset, HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdChironPosition, HdChironRole, resolveHdChironPosition } from '../hd-chiron.models';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import { HdChartPanelViewMode } from '../hd-chart-panel-view-mode';
import { HdCycleGateLineInfoRequest } from '../channel-list/hd-cycle-gate-keynote-line.component';
import { HdDefinitionKey } from '../basic-properties/hd-chart-properties.types';
import {
  HdLifeCross,
  deriveDefinition,
  deriveIncarnationCross,
  deriveLifeCross,
  derivePrimaryConditioning,
  deriveProfile,
  formatHdIncarnationCrossValue,
  formatHdLifeCrossValue,
  formatHdProfileValue,
} from '../basic-properties/hd-chart-properties.util';
import {
  HdChartMatchKind,
  HdChartMatchesService,
} from '../../library/hd-chart-matches.service';
import {
  HD_CHART_MATCH_LIST_IDLE,
  HdChartMatchListComponent,
  HdChartMatchListState,
} from '../../library/hd-chart-match-list.component';
import { HdChartMatchToggleComponent } from '../../library/hd-chart-match-toggle.component';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { HD_CYCLE_KEYS, HdCycleCalculateRequest, HdCycleKey, HdCyclePassageV1 } from '../hd-cycle-chart.models';
import { HdChartMatchScope } from '../chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../hd-content-language.service';
import { filterHdCycleOverlayActivations } from '../hd-cycle-overlay.utils';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdCyclesCheatSheetCardKey } from '../hd-cycles-cheat-sheet-metadata';
import { HdAgeSuffixComponent } from './hd-age-suffix.component';
import { HD_CHIRON_DISPLAY_CONFIG } from '../planet-grid/hd-planet-display-config';

/** Laad-status van één uitklapbare matchlijst (profiel of incarnatiekruis). */
type HdCycleMatchListState = HdChartMatchListState;

const MATCH_LIST_IDLE: HdCycleMatchListState = HD_CHART_MATCH_LIST_IDLE;

/** Klik op het info-icoon van de definitie-rij van natal + cyclus. */
export interface HdCycleDefinitionInfoRequest {
  readonly definitionKey: HdDefinitionKey;
  readonly origin: { x: number; y: number };
}

/** Cyclus-specifieke hint-variant: een split splitst verder in klein/lang. */
type HdCycleDefinitionHintKey = Exclude<HdDefinitionKey, 'split' | 'none'> | 'smallSplit' | 'longSplit';

/** Aantal definitie-eilanden per definitie (geen definitie = 0). */
const DEFINITION_AREAS: Readonly<Record<HdDefinitionKey, number>> = {
  none: 0,
  single: 1,
  split: 2,
  tripleSplit: 3,
  quadrupleSplit: 4,
};

/**
 * Rij-sleutel: de cycli plus "Just Now Transit". Die laatste is geen cycle
 * (geen CycleKey in de library) maar een momentchart: enkel de personality-
 * kant van nu over deze chart, zoals "Moment bewaren" na de Just Now-transit.
 */
type CycleRowKey = HdCycleKey | 'justNowTransit';

interface CycleRowVm {
  key: CycleRowKey;
  icon: string;
  /** Astronomicon-glyph van de cyclusplaneet; null bij Just now. */
  glyph: string | null;
  label: string;
  /** Prominente datum (browsertijd); null bij Just now → toon "nu". */
  momentText: string | null;
  tooltip: string | null;
  passagesBadge: string | null;
  /** Gevuld ⇔ al opgeslagen → "Openen" (herkend via hasCycle-relaties). */
  savedChartId: string | null;
  /** null bij Just Now Transit: die rij vraagt een momentchart aan, geen cycle. */
  request: HdCycleCalculateRequest | null;
}

/** Klik op een poort.lijn-chip in het Chiron-drieluik: opent de poortinfo met die Chiron-positie. */
export interface HdCycleChironInfoRequest {
  readonly position: HdChironPosition;
  readonly role: HdChironRole;
  readonly origin: { x: number; y: number };
}

/** Eén stap van het Chiron-drieluik (wond → werk → resolutie) in de Chiron return-info. */
interface ChironStepVm {
  readonly role: HdChironRole;
  readonly icon: string;
  readonly title: string;
  readonly source: string;
  readonly text: string;
  /** Positie van deze persoon; null wanneer de chart (nog) geen Chiron-data bevat. */
  readonly position: HdChironPosition | null;
  readonly side: HdChironPosition['side'];
  readonly gateTooltip: string;
}

/** Klik op het info-icoon van de levenskruis-rij van het cycle-moment. */
export interface HdCycleLifeCrossInfoRequest {
  readonly lifeCross: HdLifeCross;
  /** Cyclusnaam + moment, bv. "Solar return (27 september 2026, 14:03)". */
  readonly cycleLabel: string;
}

const T = 'humanDesign.advancedPanel.cycles';
const MOMENT_FORMAT = 'D MMMM YYYY, HH:mm';

/** Optie in de Jupiter-return-kiezer: volgnummer + afgeronde leeftijd ("2e · 24 j"). */
interface JupiterReturnOption {
  readonly value: number;
  readonly label: string;
}

/**
 * Cheat-sheet-kaart van een cyclus: de tweede Saturnus-return deelt de
 * Saturn-return-kaart; de Uranus- en Jupiter-return staan niet op de cheat sheet
 * (null → subtitel en bullets komen volledig uit de app-only `appInfo`-tak).
 */
function cheatSheetCardKeyOf(key: Exclude<HdCycleKey, 'justNow'>): HdCyclesCheatSheetCardKey | null {
  switch (key) {
    case 'secondSaturnReturn':
      return 'saturnReturn';
    case 'uranusReturn':
    case 'jupiterReturn':
      return null;
    default:
      return key;
  }
}

/**
 * Cycles-sectie in het Geavanceerd-paneel (spec cycle charts §9). Op een
 * Standard-chart: de cycli met moment + Berekenen/Openen; op een geladen
 * cycle chart: de prominente cyclus-header met uitklapbare cheat-sheet-info,
 * het profiel, incarnatiekruis en levenskruis van het cycle-moment en de verouderd-status
 * (§6.1; de natal-link staat in de Eigenschappen-sectie). Verborgen voor
 * andere charttypen (JustNow).
 */
@Component({
  selector: 'hd-cycles-section',
  standalone: true,
  imports: [FormsModule, NgSelectModule, NgTemplateOutlet, HdAgeSuffixComponent, HdChartMatchListComponent, HdChartMatchToggleComponent],
  templateUrl: './hd-cycles-section.component.html',
  styleUrl: './hd-cycles-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCyclesSectionComponent {
  /** Library-chart-id van de actieve tab; null = niet opgeslagen → eerst opslaan. */
  readonly savedChartId = input<string | null>(null);
  /** Weergavestand (spec 2026-10-20-chart-share §4.5): shared/guest → geen library-lijst; guest → klik meldt "account nodig". */
  readonly viewMode = input<HdChartPanelViewMode>('owner');
  readonly chartType = input<PlatformChartType>(PlatformChartType.Standard);
  readonly preset = input<HdBodygraphChartPreset | null>(null);
  /** Relaties van de actieve chart (het paneel laadt ze al; geen dubbele fetch). */
  readonly relations = input<PlatformChartRelationDto[]>([]);
  /** Scope van de "gelijkaardige charts"-matchlijsten (globale setting). */
  readonly matchScope = input<HdChartMatchScope>('library');
  /** Naam van de actieve chart: op een momentchart is dat de "cyclusnaam". */
  readonly chartName = input('');

  readonly calculateRequested = output<HdCycleCalculateRequest>();
  /** Rij "Just Now Transit": het huidige moment als momentchart (enkel personality) over deze chart. */
  readonly justNowTransitRequested = output<void>();
  readonly chartOpenRequested = output<string>();
  readonly recalculateRequested = output<void>();
  /** Gaststand: een klik die normaal een paneel opent; de host meldt dat een account nodig is. */
  readonly accountRequired = output<void>();

  /** Gast: niets openen, wel melden. True = de aanroeper stopt. */
  private guestBlocked(event?: Event): boolean {
    if (this.viewMode() !== 'guest') return false;
    event?.preventDefault();
    event?.stopPropagation();
    this.accountRequired.emit();
    return true;
  }
  /**
   * Info-icoon op de levenskruis-rij van het cycle-moment: opent het nodale-
   * omgevingen-paneel met de nodes van de cyclus (vooral bij solar returns
   * speelt de nodale omgeving van dat jaar een grote rol).
   */
  readonly lifeCrossInfoRequested = output<HdCycleLifeCrossInfoRequest>();
  /** Info-icoon op de definitie-rij: opent het definitie-paneel met de gecombineerde definitie als "jouw definitie". */
  readonly definitionInfoRequested = output<HdCycleDefinitionInfoRequest>();
  /** Klik op een brugpoort van de gecombineerde definitie: opent de poort-info. */
  readonly gateInfoRequested = output<HdCycleGateLineInfoRequest>();
  /** Klik op een Chiron-positie in het drieluik van de Chiron return-info. */
  readonly chironInfoRequested = output<HdCycleChironInfoRequest>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  /** Cycle-momenten en de natal-meta voor de verouderd-check komen uit de per-chart datacache. */
  private readonly dataCache = inject(HdChartDataCache);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly chartMatches = inject(HdChartMatchesService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);

  /** Gekozen solar-return-jaar; null = default (eerstvolgende) van de backend. */
  protected readonly solarYear = signal<number | null>(null);

  /** Gekozen Jupiter-return (volgnummer); null = default: de eerstvolgende. */
  protected readonly jupiterNumber = signal<number | null>(null);

  /** Cache-sleutel van de getoonde momenten (chart + jaar); null buiten de standaardweergave. */
  private readonly momentsKey = computed(() => {
    const chartId = this.savedChartId();
    return this.isStandardView() && chartId ? cycleMomentsKey(chartId, this.solarYear()) : null;
  });
  protected readonly moments = computed<PlatformChartCycleMomentsDto | null>(() =>
    this.dataCache.cycleMoments.value(this.momentsKey()) ?? null);
  protected readonly momentsLoading = computed(() => {
    const entry = this.dataCache.cycleMoments.entry(this.momentsKey());
    return !!entry?.loading && entry.value === undefined;
  });
  protected readonly momentsError = computed(() => {
    const entry = this.dataCache.cycleMoments.entry(this.momentsKey());
    return entry?.value === undefined ? (entry?.error ?? '') : '';
  });

  /** Verouderd ⇔ de natal-kern wijkt af van de snapshot in het document (spec §6.1). */
  protected readonly staleState = computed<'unknown' | 'fresh' | 'stale' | 'missing'>(() => {
    const cycle = this.preset()?.cycle;
    if (!cycle) return 'unknown';
    const entry = this.dataCache.chartMeta.entry(cycle.source.chartId);
    // Fout zonder (actuele) waarde: bronchart weg of onbereikbaar.
    if (entry?.error && (entry.value === undefined || entry.stale)) return 'missing';
    const meta = entry?.value;
    if (!meta) return 'unknown';
    const source = cycle.source;
    const sameBirth = this.sameInstant(source.birthDateUtc, meta.birthDateUtc ?? null)
      && this.sameNumber(source.latitude, meta.latitude ?? null)
      && this.sameNumber(source.longitude, meta.longitude ?? null)
      && (source.timezone ?? null) === (meta.timezone ?? null);
    return sameBirth ? 'fresh' : 'stale';
  });

  /** Cheat-sheet-infotekst van de cyclus uitgeklapt? (default dicht). */
  protected readonly cycleCardInfoExpanded = signal(false);

  /** Uitgeklapte matchlijsten (profiel/kruis). */
  protected readonly expandedMatches = signal<ReadonlySet<HdChartMatchKind>>(new Set());

  /** Laad-status + items per matchlijst; lazy geladen bij de eerste uitklap. */
  protected readonly matchStates = signal<Readonly<Record<HdChartMatchKind, HdCycleMatchListState>>>({
    profile: MATCH_LIST_IDLE,
    incarnationCross: MATCH_LIST_IDLE,
  });

  constructor() {
    // Beide content-talen eager laden zodat de NL-fallback voor nog niet
    // vertaalde EN-cheat-sheet-velden meteen correcte tekst oplevert.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
    // Andere cyclus of andere match-scope → cache en uitklap-status resetten,
    // zodat de volgende uitklap verse resultaten laadt.
    effect(() => {
      this.preset();
      this.matchScope();
      this.expandedMatches.set(new Set());
      this.matchStates.set({ profile: MATCH_LIST_IDLE, incarnationCross: MATCH_LIST_IDLE });
    });
    effect(onCleanup => {
      const key = this.momentsKey();
      if (key) onCleanup(this.dataCache.cycleMoments.watch(key));
    });
    effect(onCleanup => {
      const sourceChartId = this.preset()?.cycle?.source.chartId;
      // Gedeelde chart: de bronchart is van een andere eigenaar (geen call; gast heeft geen JWT).
      if (sourceChartId && this.viewMode() === 'owner') onCleanup(this.dataCache.chartMeta.watch(sourceChartId));
    });
  }

  protected readonly isCycleView = computed(() => this.preset()?.cycle != null);
  /**
   * Momentchart (transit-blok) als cyclus (spec transits 2026-09-26 §2.8): de
   * cyclus-header met de chartnaam als cyclusnaam en het tijdstip van het
   * moment. Geen cheat-sheet-info, geen verouderd-controle (een moment wordt
   * niet herberekend) en geen profiel/kruis (die vergen een design-kant).
   */
  protected readonly isMomentView = computed(() => !this.isCycleView() && this.preset()?.transit != null);
  protected readonly isStandardView = computed(
    () => !this.isCycleView() && !this.isMomentView() && this.chartType() === PlatformChartType.Standard,
  );
  protected readonly visible = computed(() => this.isStandardView() || this.isCycleView() || this.isMomentView());

  protected readonly text = computed(() => {
    const translate = this.contentTranslate();
    return {
      title: translate(`${T}.title`),
      saveFirst: translate(`${T}.saveFirst`),
      sharedUnavailable: translate('humanDesign.advancedPanel.sharedUnavailable'),
      refresh: translate(`${T}.refresh`),
      calculate: translate(`${T}.calculate`),
      open: translate(`${T}.open`),
      now: translate(`${T}.now`),
      loadError: translate(`${T}.loadError`),
      actuations: translate(`${T}.actuations`),
      yearPlaceholder: translate(`${T}.yearPlaceholder`),
      jupiterReturnPlaceholder: translate(`${T}.jupiterReturnPlaceholder`),
      momentLabel: translate(`${T}.moment`),
      outdated: translate(`${T}.outdated`),
      outdatedTooltip: translate(`${T}.outdatedTooltip`),
      recalculate: translate(`${T}.recalculate`),
      natalUnavailable: translate(`${T}.natalUnavailable`),
      loading: translate('humanDesign.chartManager.detail.loading'),
      matchesProfileTooltip: translate(`${T}.matchesProfileTooltip`),
      matchesCrossTooltip: translate(`${T}.matchesCrossTooltip`),
      matchesEmpty: translate(`${T}.matchesEmpty`),
      matchesError: translate(`${T}.matchesError`),
    };
  });

  private readonly birthMoment = computed(() => {
    const core = extractCycleSourceCore(this.preset()?.birthData ?? null);
    return core.birthDateUtc ? moment.utc(core.birthDateUtc) : null;
  });

  /** Jaaropties voor de solar-return-kiezer: geboortejaar+1 t/m nu+80. */
  protected readonly yearOptions = computed<number[]>(() => {
    const birth = this.birthMoment();
    if (!birth) return [];
    const start = birth.year() + 1;
    const end = moment().year() + 80;
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
  });

  protected readonly selectedYear = computed(() => this.solarYear() ?? this.moments()?.solarReturnYear ?? null);

  /**
   * Alle Jupiter-returns van een leven (de backend levert ze genummerd onder
   * dezelfde key), oplopend; de kiezer toont er één tegelijk.
   */
  private readonly jupiterReturns = computed(() =>
    (this.moments()?.cycles ?? [])
      .filter(cycle => cycle.key === 'jupiterReturn' && cycle.returnNumber != null && cycle.primaryUtc)
      .sort((a, b) => a.returnNumber! - b.returnNumber!));

  protected readonly jupiterOptions = computed<JupiterReturnOption[]>(() => {
    const birth = this.birthMoment();
    const lang = this.effectiveContentLang();
    return this.jupiterReturns().map(cycle => {
      const number = cycle.returnNumber!;
      const age = birth ? Math.round(moment.utc(cycle.primaryUtc).diff(birth, 'years', true)) : null;
      const labelKey = age != null ? 'jupiterReturnOption' : 'jupiterReturnOptionNoAge';
      return { value: number, label: this.transloco.translate<string>(`${T}.${labelKey}`, { number, age }, lang) };
    });
  });

  /** Gekozen Jupiter-return, anders de eerstvolgende na nu (zoals de solar return), of de laatste. */
  protected readonly selectedJupiterNumber = computed<number | null>(() => {
    const returns = this.jupiterReturns();
    const chosen = this.jupiterNumber();
    if (chosen != null && returns.some(cycle => cycle.returnNumber === chosen)) return chosen;
    const now = moment.utc();
    const upcoming = returns.find(cycle => moment.utc(cycle.primaryUtc).isAfter(now));
    return (upcoming ?? returns[returns.length - 1])?.returnNumber ?? null;
  });

  private readonly savedCycleRelations = computed(() =>
    this.relations().filter(relation => relation.relationType === 'hasCycle'),
  );

  protected readonly rows = computed<CycleRowVm[]>(() => {
    const translate = this.contentTranslate();
    const dto = this.moments();
    const jupiterNumber = this.selectedJupiterNumber();
    const cycleRows = HD_CYCLE_KEYS.map((key): CycleRowVm => {
      const cycle = dto?.cycles?.find(c => c.key === key
        && (key !== 'jupiterReturn' || c.returnNumber === jupiterNumber)) ?? null;
      const passages: HdCyclePassageV1[] = (cycle?.passages ?? [])
        .map(p => ({ utc: p.utc ?? '', retrograde: p.retrograde ?? false }));
      const anchorUtc = key === 'justNow' ? null : (cycle?.primaryUtc ?? null);
      return {
        key,
        icon: HD_CYCLE_ICONS[key],
        glyph: HD_CYCLE_GLYPHS[key],
        label: hdCycleLabel(translate, key),
        momentText: anchorUtc ? moment.utc(anchorUtc).local().format('D MMM YYYY') : null,
        tooltip: key === 'justNow' ? translate(`${T}.justNowFullTooltip`) : this.buildTooltip(anchorUtc, passages),
        passagesBadge: passages.length > 1
          ? this.transloco.translate<string>(`${T}.passages`, { count: passages.length }, this.contentLang.effectiveLang())
          : null,
        savedChartId: this.findSavedChartId(key, anchorUtc),
        request: {
          key,
          anchorUtc,
          passages,
          ...(key === 'solarReturn' && this.selectedYear() != null ? { solarReturnYear: this.selectedYear()! } : {}),
          ...(key === 'jupiterReturn' && jupiterNumber != null ? { jupiterReturnNumber: jupiterNumber } : {}),
        },
      };
    });
    const justNowTransitRow: CycleRowVm = {
      key: 'justNowTransit',
      icon: HD_JUST_NOW_ICONS.transit,
      glyph: null,
      label: hdCycleLabel(translate, 'justNowTransit'),
      momentText: null,
      tooltip: translate(`${T}.justNowTransitTooltip`),
      passagesBadge: null,
      savedChartId: null, // elk nu-moment is nieuw — altijd berekenen
      request: null,
    };
    return [...cycleRows, justNowTransitRow];
  });

  /** Cycle-chart-weergave: info uit het eigen document; een momentchart levert naam + tijdstip. */
  protected readonly cycleInfo = computed(() => {
    const preset = this.preset();
    const cycle = preset?.cycle;
    if (cycle) {
      const translate = this.contentTranslate();
      const age = this.ageAtCycleMoment(cycle.source.birthDateUtc, cycle.anchorUtc);
      const lang = this.effectiveContentLang();
      return {
        label: hdCycleLabel(translate, cycle.key),
        icon: HD_CYCLE_ICONS[cycle.key] ?? 'arrows-rotate',
        glyph: HD_CYCLE_GLYPHS[cycle.key] ?? null,
        momentText: moment.utc(cycle.anchorUtc).local().format(MOMENT_FORMAT),
        tooltip: this.buildTooltip(cycle.anchorUtc, cycle.passages),
        // Leeftijd als gedeeld "· 13 jaar"-suffix achter de datum (zelfde
        // weergave als de Eigenschappen-rijen; hd-age-suffix vertaalt zelf).
        ageYears: age,
        ageTooltip: this.cyclesText('appInfo.ageTooltip', lang),
      };
    }
    const transit = preset?.transit;
    if (!transit) return null;
    const start = moment.utc(transit.momentUtc).local().format(MOMENT_FORMAT);
    return {
      label: this.chartName().trim() || preset?.name || '',
      icon: 'calendar-star',
      glyph: null,
      momentText: transit.momentEndUtc
        ? `${start} – ${moment.utc(transit.momentEndUtc).local().format(MOMENT_FORMAT)}`
        : start,
      tooltip: null,
      ageYears: null,
      ageTooltip: '',
    };
  });

  /** Amber subsectie-titel boven de cyclus-header ("Algemene cyclus/moment info"). */
  protected readonly generalInfoTitle = computed(() =>
    this.cyclesText(
      this.isMomentView() ? 'ui.generalMomentInfoTitle' : 'ui.generalCycleInfoTitle',
      this.effectiveContentLang(),
    ),
  );

  /**
   * Cheat-sheet-infotekst van de gedetecteerde cyclus (subtitel + bullets uit de
   * `human-design/cycles`-scope). Alleen voor de bekende vaste cycli — Just now
   * (en dus ook een losse-datum-cyclus) heeft geen infotekst; de tweede
   * Saturnus-return deelt de Saturn-return-content.
   *
   * Daaronder de app-only verdieping uit `appInfo` (content-ronde 2026-09-30):
   * de cheat-sheet-kaarten zijn vol, dus tijdstip, extra bullets, een valkuil-blok, de 6de-lijn-
   * regels, de zuid→noord-kanteling van de natal nodes (Uranus-oppositie) en de
   * kruishoek van het cyclusmoment staan enkel hier. De tweede Saturnus-return
   * heeft een eigen `appInfo`-tak (tijdstip + eigen noot): de Saturn-return-verdieping
   * gaat over de eerste return. De Uranus-return heeft geen cheat-sheet-kaart: zijn
   * `appInfo`-tak levert dan zelf subtitel en bullets (zonder verdiepingsscheiding).
   */
  protected readonly cycleCardInfo = computed(() => {
    const preset = this.preset();
    const cycle = preset?.cycle;
    if (!cycle || cycle.key === 'justNow') return null;
    const cardKey = cheatSheetCardKeyOf(cycle.key);
    const lang = this.effectiveContentLang();
    const appKey = `appInfo.cycles.${cycle.key}`;
    const appBullets = this.cyclesList(`${appKey}.bullets`, lang);
    const bullets = cardKey
      ? [...this.cyclesList(`cards.${cardKey}.bullets`, lang), ...this.cyclesList(`cards.${cardKey}.bullets2`, lang)]
      : appBullets;
    if (bullets.length === 0) return null;
    const natalActivations = preset.planetaryActivations ?? [];
    const extraBullets = cardKey ? appBullets : [];
    // Valkuil-blok (solar return: de return is geen eigen chart; Ra, Life Cycles Analysis).
    const gotchaBullets = this.cyclesList(`${appKey}.gotcha.bullets`, lang);
    const gotcha = gotchaBullets.length > 0
      ? { title: this.cyclesText(`${appKey}.gotcha.title`, lang), bullets: gotchaBullets }
      : null;
    const sixthLine = this.sixthLineInfo(`${appKey}.sixthLine`, natalActivations, lang);
    const nodeShift = cycle.key === 'uranusOpposition' ? this.natalNodeShift(natalActivations, lang) : null;
    const crossAngle = this.cycleCrossAngle(cycle.moment.planetaryActivations, lang);
    return {
      subtitle: this.cyclesText(cardKey ? `cards.${cardKey}.subtitle` : `${appKey}.subtitle`, lang),
      bullets,
      timing: this.cyclesText(`${appKey}.timing`, lang),
      extraBullets,
      gotcha,
      sixthLine,
      nodeShift,
      crossAngle,
      /** Iets onder de cheat-sheet-bullets? → scheidingslijn + verdiepingsblok. */
      hasExtra: extraBullets.length > 0 || gotcha != null || sixthLine != null || nodeShift != null || crossAngle != null,
      showLabel: this.cyclesText('ui.showInfo', lang),
      hideLabel: this.cyclesText('ui.hideInfo', lang),
    };
  });

  /**
   * Chiron-drieluik in de Chiron return-info (techniek van Genoa Bliven, lezing
   * LaVeena 2026-10-01): de natal personality-Chiron toont het thema van de wond,
   * de natal design-Chiron het werk om te helen en de design-Chiron van de return
   * chart de resolutie. Elke stap toont de poort.lijn van deze persoon (klikbaar
   * naar de poortinfo); zonder Chiron-data blijft de uitleg zonder chip staan.
   * De personality-Chiron van de return valt per definitie op de natal positie,
   * dus die is geen aparte stap.
   */
  protected readonly chironPositions = computed(() => {
    const preset = this.preset();
    const cycle = preset?.cycle;
    if (!cycle || cycle.key !== 'chironReturn') return null;
    const lang = this.effectiveContentLang();
    const base = 'appInfo.chironPositions';
    const wound = resolveHdChironPosition(preset.metadata, 'personality');
    const work = resolveHdChironPosition(preset.metadata, 'design');
    const resolution = resolveHdChironPosition(cycle.moment.metadata, 'design');
    const openGate = this.cyclesText(`${base}.openGate`, lang);
    const step = (role: HdChironRole, icon: string, side: HdChironPosition['side'], position: HdChironPosition | null): ChironStepVm => ({
      role,
      icon,
      title: this.cyclesText(`${base}.steps.${role}.title`, lang),
      source: this.cyclesText(`${base}.steps.${role}.source`, lang),
      text: this.cyclesText(`${base}.steps.${role}.text`, lang),
      position,
      side,
      gateTooltip: position ? openGate.replaceAll('{gate}', `${position.gate}.${position.line}`) : '',
    });
    const sameSpot = work != null && resolution != null && work.gate === resolution.gate && work.line === resolution.line;
    return {
      // Kern (Ra, lezing LaVeena 2026-10-01): geen gewone kaart maar een kaart van je
      // ware zelf; een wijsheidspotentieel dat je niet "wordt" (overlay, geen identiteit).
      essence: {
        title: this.cyclesText(`${base}.essence.title`, lang),
        quote: this.cyclesText(`${base}.essence.quote`, lang),
        quoteSource: this.cyclesText(`${base}.essence.quoteSource`, lang),
        is: this.cyclesText(`${base}.essence.is`, lang),
        isNot: this.cyclesText(`${base}.essence.isNot`, lang),
      },
      title: this.cyclesText(`${base}.title`, lang),
      intro: this.cyclesText(`${base}.intro`, lang),
      steps: [
        step('wound', 'heart-crack', 'personality', wound),
        step('work', 'seedling', 'design', work),
        step('resolution', 'key', 'design', resolution),
      ],
      sameSpot: sameSpot ? this.cyclesText(`${base}.sameSpot`, lang) : '',
      notes: this.cyclesList(`${base}.notes`, lang),
      sourceNote: this.cyclesText(`${base}.sourceNote`, lang),
    };
  });

  /** Astronomicon-glyph van Chiron (titel en poort-chips van het drieluik). */
  protected readonly chironGlyph = HD_CHIRON_DISPLAY_CONFIG.astronomiconChar;

  protected requestChironInfo(step: ChironStepVm, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    if (!step.position) return;
    this.chironInfoRequested.emit({
      position: step.position,
      role: step.role,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  /**
   * 6de-lijn-regels van een cyclus, enkel wanneer het natal profiel een 6 draagt
   * (6/2, 6/3, 3/6, 4/6): het driedelige 6de-lijnproces loopt langs de Saturn- en
   * Kiron-return. De titel noemt het profiel zodat duidelijk is waarom de regels er staan.
   */
  private sixthLineInfo(
    key: string,
    natalActivations: readonly HdBodygraphPlanetaryActivation[],
    lang: string,
  ): { title: string; bullets: readonly string[] } | null {
    const profile = deriveProfile(natalActivations);
    if (!profile || (profile.personalityLine !== 6 && profile.designLine !== 6)) return null;
    const bullets = this.cyclesList(key, lang);
    if (bullets.length === 0) return null;
    const profileText = `${profile.personalityLine}/${profile.designLine}`;
    return {
      title: this.cyclesText('appInfo.sixthLineTitle', lang).replaceAll('{profile}', profileText),
      bullets,
    };
  }

  /**
   * Zuid→noord-kanteling van de natal nodes, per kant ("P 58 → 52 · D 38 → 39"):
   * rond de Uranus-oppositie verschuift de nadruk van de zuid- naar de noord-node.
   * Het info-icoon opent de nodale omgevingen van de natal chart.
   */
  private natalNodeShift(
    natalActivations: readonly HdBodygraphPlanetaryActivation[],
    lang: string,
  ): { label: string; value: string; tooltip: string; lifeCross: HdLifeCross } | null {
    const lifeCross = deriveLifeCross(natalActivations);
    if (!lifeCross) return null;
    const { personality: p, design: d } = lifeCross;
    return {
      label: this.cyclesText('appInfo.nodeShiftLabel', lang),
      value: `P ${p.southGate} → ${p.northGate} · D ${d.southGate} → ${d.northGate}`,
      tooltip: this.cyclesText('appInfo.nodeShiftTooltip', lang),
      lifeCross,
    };
  }

  /** Hoek van het incarnatiekruis (IX) op het cyclusmoment, met korte duiding. */
  private cycleCrossAngle(
    momentActivations: readonly HdBodygraphPlanetaryActivation[],
    lang: string,
  ): { label: string; text: string; tooltip: string } | null {
    const cross = deriveIncarnationCross(filterHdCycleOverlayActivations(momentActivations));
    if (!cross) return null;
    const text = this.cyclesText(`appInfo.crossAngles.${cross.angleKey}`, lang);
    if (!text) return null;
    return {
      label: this.cyclesText('appInfo.crossAngleLabel', lang),
      text,
      tooltip: this.cyclesText('appInfo.crossAngleTooltip', lang),
    };
  }

  /** Volle jaren tussen geboorte en cyclusmoment; null zonder (geldige) geboortedatum. */
  private ageAtCycleMoment(birthDateUtc: string | null, anchorUtc: string): number | null {
    if (!birthDateUtc) return null;
    const age = moment.utc(anchorUtc).diff(moment.utc(birthDateUtc), 'years');
    return Number.isFinite(age) && age >= 0 ? age : null;
  }

  /** Info-icoon op de kantel-rij (Uranus-oppositie): nodale omgevingen van de natal chart. */
  protected requestNatalLifeCrossInfo(lifeCross: HdLifeCross): void {
    if (this.guestBlocked()) return;
    // Lege cycleLabel = geen "van het cyclusmoment"-contextregel: dit zijn de natal nodes.
    this.lifeCrossInfoRequested.emit({ lifeCross, cycleLabel: '' });
  }

  protected toggleCycleCardInfo(): void {
    this.cycleCardInfoExpanded.update(expanded => !expanded);
  }

  /** Ruwe matchwaarden van het cycle-moment: profiel "P/D" + canonieke kruis-key. */
  private readonly cycleMatchSource = computed(() => {
    const cycle = this.preset()?.cycle;
    if (!cycle) return null;
    const activations = filterHdCycleOverlayActivations(cycle.moment.planetaryActivations);
    const profile = deriveProfile(activations);
    const cross = deriveIncarnationCross(activations);
    return {
      sourceChartId: cycle.source.chartId || null,
      profileValue: profile ? `${profile.personalityLine}/${profile.designLine}` : '',
      crossValue: cross ? `${cross.angleKey}-${cross.gates.join('-')}` : '',
    };
  });

  protected isMatchesExpanded(kind: HdChartMatchKind): boolean {
    return this.expandedMatches().has(kind);
  }

  protected matchState(kind: HdChartMatchKind): HdCycleMatchListState {
    return this.matchStates()[kind];
  }

  /** Klik op het match-icoon: uitklappen (met lazy load bij de eerste keer) of inklappen. */
  protected toggleMatches(kind: HdChartMatchKind): void {
    if (this.guestBlocked()) return;
    const willExpand = !this.expandedMatches().has(kind);
    this.expandedMatches.update(expanded => {
      const next = new Set(expanded);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
    if (willExpand && this.matchStates()[kind].status === 'idle') {
      void this.loadMatches(kind);
    }
  }

  protected openMatch(item: PlatformChartListItemDto): void {
    if (this.guestBlocked()) return;
    if (item.id) this.chartOpenRequested.emit(item.id);
  }

  private async loadMatches(kind: HdChartMatchKind): Promise<void> {
    const source = this.cycleMatchSource();
    if (!source) return;
    const value = kind === 'profile' ? source.profileValue : source.crossValue;
    this.setMatchState(kind, { status: 'loading', items: [] });
    try {
      const items = await this.chartMatches.findMatches({
        kind,
        value,
        scope: this.matchScope(),
        sourceChartId: source.sourceChartId,
        excludeChartIds: [source.sourceChartId, this.savedChartId()]
          .filter((id): id is string => id != null),
      });
      this.setMatchState(kind, { status: 'loaded', items: items.map(item => ({ item })) });
    } catch {
      this.setMatchState(kind, { status: 'error', items: [] });
    }
  }

  private setMatchState(kind: HdChartMatchKind, state: HdCycleMatchListState): void {
    this.matchStates.update(states => ({ ...states, [kind]: state }));
  }

  /**
   * Profiel, incarnatiekruis en levenskruis van het cycle-moment zelf (zoals
   * het Basis-paneel die van een persoonlijke chart toont), onder de
   * natal-regel. `null` zolang het moment geen volledige zon/aarde-activaties
   * draagt; de levenskruis-rij valt enkel weg zonder node-activaties.
   */
  protected readonly cycleMomentProperties = computed(() => {
    const cycle = this.preset()?.cycle;
    if (!cycle) return null;
    const activations = filterHdCycleOverlayActivations(cycle.moment.planetaryActivations);
    const lang = this.effectiveContentLang();
    const t = (key: string) =>
      this.transloco.translate<string>(`humanDesign.properties.${key}`, undefined, lang);
    const profileText = formatHdProfileValue(deriveProfile(activations), t);
    const crossText = formatHdIncarnationCrossValue(
      deriveIncarnationCross(activations), t, lang, this.gateInfo,
    );
    const lifeCross = deriveLifeCross(activations);
    if (!profileText && !crossText && !lifeCross) return null;
    return {
      profileLabel: t('profile'),
      profileText,
      crossLabel: t('incarnationCross'),
      crossText,
      lifeCross,
      // Bij cycles heet de rij "Nodale omgeving" (wens Steven): de nodes van het
      // cyclusmoment tonen vooral de omgeving van die periode.
      lifeCrossLabel: this.transloco.translate<string>(`${T}.nodalEnvironment`, undefined, lang),
      lifeCrossText: formatHdLifeCrossValue(lifeCross),
      lifeCrossInfoTooltip: t('lifeCrossInfoTooltip'),
    };
  });

  /**
   * Definitie van de volledige cycle chart (natal + cyclusmoment samen), onder
   * de nodale omgeving: de waarde, de natal-definitie wanneer die verschilt,
   * de brugpoorten bij een split en een cyclus-specifieke hint (vooral: waar
   * je deze periode niet naar op zoek hoeft te gaan).
   */
  protected readonly cycleDefinition = computed(() => {
    const preset = this.preset();
    if (!preset?.cycle) return null;
    const combinedState = this.renderStateFactory.create(mergeCycleConfiguration(preset));
    const definitionKey = deriveDefinition(combinedState);
    if (definitionKey === 'none') return null;
    const natalKey = deriveDefinition(this.renderStateFactory.create(preset.configuration));
    // Rangschikking van de brugpoorten volgt de primaire activaties van de
    // persoon zelf (natal), niet de samengevoegde twee zonnen/aardes.
    const conditioning = derivePrimaryConditioning(combinedState, preset.planetaryActivations ?? []);
    const hintKey: HdCycleDefinitionHintKey = definitionKey === 'split'
      ? (conditioning.kind === 'bridgeGates' ? 'smallSplit' : 'longSplit')
      : definitionKey;
    // Meer eilanden dan natal: de (extra) split ontstaat door de cyclus zelf. Volgens
    // Ra werkt zo'n split niet als split en zijn de brugpoorten geen thema (Life Cycles
    // Analysis, Q&A "Is the Split Created by the Solar Return Important?").
    const cycleCreatedSplit = DEFINITION_AREAS[definitionKey] >= 2
      && DEFINITION_AREAS[definitionKey] > DEFINITION_AREAS[natalKey];

    const lang = this.effectiveContentLang();
    const t = (key: string) =>
      this.transloco.translate<string>(`humanDesign.properties.${key}`, undefined, lang);
    const splitSuffix = definitionKey === 'split' ? ` (${this.cyclesText(`definition.${hintKey}`, lang)})` : '';
    const bridgeGates = conditioning.kind === 'bridgeGates'
      ? conditioning.gates.map(bridge => [bridge.gate])
      : conditioning.kind === 'bridgePairs'
        ? conditioning.pairs.map(pair => pair.map(bridge => bridge.gate))
        : [];
    return {
      definitionKey,
      label: t('definition'),
      text: `${t(`definitionValues.${definitionKey}`)}${splitSuffix}`,
      natalText: natalKey !== definitionKey
        ? `${this.cyclesText('definition.natalPrefix', lang)} ${t(`definitionValues.${natalKey}`)}`
        : '',
      infoTooltip: t('definitionInfoTooltip'),
      // De hints per definitie staan voorlopig leeg in de cycles-scope (2026-09-30: in een
      // cyclus-context vragen ze eigen, nog te schrijven teksten); leeg = niet getoond.
      // Een split die de cyclus zelf maakt, krijgt wel altijd zijn eigen hint.
      hint: cycleCreatedSplit
        ? this.cyclesText('definition.hints.cycleCreatedSplit', lang)
        : this.cyclesText(`definition.hints.${hintKey}`, lang),
      bridgeLabel: conditioning.kind === 'bridgePairs'
        ? this.cyclesText('definition.bridgePairsLabel', lang)
        : this.cyclesText('definition.bridgeGatesLabel', lang),
      bridgeGates,
    };
  });

  protected requestDefinitionInfo(definitionKey: HdDefinitionKey, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.definitionInfoRequested.emit({ definitionKey, origin: { x: event.clientX, y: event.clientY } });
  }

  protected requestGateInfo(gate: number, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
  }

  protected requestLifeCrossInfo(lifeCross: HdLifeCross): void {
    if (this.guestBlocked()) return;
    const info = this.cycleInfo();
    const cycleLabel = info ? `${info.label} (${info.momentText})` : '';
    this.lifeCrossInfoRequested.emit({ lifeCross, cycleLabel });
  }

  protected onYearChange(year: number | null): void {
    this.solarYear.set(year);
  }

  protected onJupiterNumberChange(returnNumber: number | null): void {
    this.jupiterNumber.set(returnNumber);
  }

  /** Verversknop in de header: enkel bij de cycles-lijst van een Standard-chart. */
  protected refresh(): void {
    if (this.guestBlocked()) return;
    const key = this.momentsKey();
    if (key) void this.dataCache.cycleMoments.refresh(key);
  }

  protected requestCalculate(row: CycleRowVm): void {
    if (this.guestBlocked()) return;
    if (row.savedChartId) {
      this.chartOpenRequested.emit(row.savedChartId);
      return;
    }
    if (row.request == null) {
      this.justNowTransitRequested.emit();
      return;
    }
    if (row.key !== 'justNow' && row.request.anchorUtc == null) return; // momenten (nog) niet geladen
    this.calculateRequested.emit(row.request);
  }

  private findSavedChartId(key: HdCycleKey, anchorUtc: string | null): string | null {
    if (key === 'justNow') return null; // elk nu-moment is nieuw — altijd berekenen
    const candidates = this.savedCycleRelations().filter(r => r.otherChartCycleKey === key);
    if (key === 'jupiterReturn') {
      // Jupiter-returns liggen ±11,86 jaar uit elkaar: een anchor binnen 2 jaar
      // van de gekozen return is die return (ook na een herberekening).
      if (anchorUtc == null) return null;
      const match = candidates.find(r => r.otherChartCycleAnchorUtc
        && Math.abs(moment.utc(r.otherChartCycleAnchorUtc).diff(moment.utc(anchorUtc), 'years', true)) < 2);
      return match?.otherChartId ?? null;
    }
    if (key !== 'solarReturn') return candidates[0]?.otherChartId ?? null;

    // Solar return: match op jaartal via de anchor rond de verjaardag (spec §7).
    const year = this.selectedYear();
    const birth = this.birthMoment();
    if (year == null || birth == null) return null;
    const birthday = birth.clone().year(year);
    const match = candidates.find(r => r.otherChartCycleAnchorUtc
      && Math.abs(moment.utc(r.otherChartCycleAnchorUtc).diff(birthday, 'days')) <= 30);
    return match?.otherChartId ?? null;
  }

  private buildTooltip(anchorUtc: string | null, passages: readonly HdCyclePassageV1[]): string | null {
    if (!anchorUtc) return null;
    const lines = [moment.utc(anchorUtc).local().format('D MMMM YYYY, HH:mm')];
    if (passages.length > 1) {
      const translate = this.contentTranslate();
      lines.push(`${translate(`${T}.actuations`)}: ${passages
        .slice(1)
        .map(p => `${moment.utc(p.utc).local().format('D MMM YYYY')} (${p.retrograde ? 'R' : 'D'})`)
        .join(', ')}`);
    }
    return lines.join('\n');
  }

  private sameInstant(a: string | null, b: string | null): boolean {
    if (!a || !b) return a === b;
    return moment.utc(a).isSame(moment.utc(b));
  }

  private sameNumber(a: number | null, b: number | null): boolean {
    if (a == null || b == null) return a === b;
    return Math.abs(a - b) < 1e-6;
  }

  private contentTranslate(): (key: string) => string {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    return (key: string) => this.transloco.translate<string>(key, undefined, lang);
  }

  private effectiveContentLang(): string {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }

  private cyclesList(key: string, lang: string): readonly string[] {
    const value = this.gateInfo.cyclesCheatSheetListIn(key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetListIn(key, 'nl');
  }
}
